import { ContactsApi, type ContactSearchHit } from "@/api/contacts/ContactsApi";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import {
  isConcreteContactMatchInput,
  type FinderDraft,
} from "@/ui/finder/finderMode";
import { useEffect, useState } from "react";
import { Text, View } from "react-native";

/** Nur nach konkreter Telefon-/E-Mail-Eingabe; keine browse-fähige Liste. */
export function FinderReuseMatch({
  organizationId,
  draft,
  onApply,
}: {
  organizationId: string | undefined;
  draft: FinderDraft;
  onApply: (hit: ContactSearchHit) => void;
}) {
  const { theme } = useTheme();
  const [hit, setHit] = useState<ContactSearchHit | null>(null);

  const concrete = isConcreteContactMatchInput({
    phone: draft.phone,
    email: draft.email,
  });

  useEffect(() => {
    if (!organizationId || !concrete) {
      setHit(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      void ContactsApi.matchExistingContacts({
        organizationId,
        phone: draft.phone.trim() || null,
        email: draft.email.trim() || null,
      })
        .then((rows) => {
          if (cancelled) return;
          setHit(rows[0] ?? null);
        })
        .catch(() => {
          if (!cancelled) setHit(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    organizationId,
    concrete,
    draft.phone,
    draft.email,
    draft.matchedContactId,
  ]);

  if (draft.matchedContactId) {
    return (
      <Text style={{ color: theme.color.muted, fontSize: 13 }}>
        Vorhandener Kontakt wird wiederverwendet.
      </Text>
    );
  }

  if (!hit) return null;

  return (
    <View style={{ gap: theme.space.xs }}>
      <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>
        Passender vorhandener Kontakt: {hit.display_name}
      </Text>
      <AppButton variant="outline" onPress={() => onApply(hit)}>
        Kontakt wiederverwenden
      </AppButton>
    </View>
  );
}
